import { Button, Form, Table } from "antd";
import Icon from "../../components/Icon";
import HrnModal from "../../components/HrnModal";
import validateMessages from "../../utils/validate";
import { LoadingOutlined } from "@ant-design/icons";
import { Actions } from "../../shared/constants";
import { useEffect, useState } from "react";
import globalUtils from "../../utils/globalUtils";
import tableUtils from "../../utils/tableUtils";
import { SubscriptionsColumn } from "../../shared/table/columns";
import SubscriptionForm from "./SubscriptionForm";
import { useCreateSubscriptionMutation, useDeleteSubscriptionMutation, useGetSubscriptionsQuery, useUpdateSubscriptionMutation } from "../../services/subscription";

const Subscription = () => {
    const [subscriptionForm] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("");
    const [editId, setEditId] = useState("");
    const [dataSource, setDataSource] = useState<any[]>([]);
    const {
        data: subscriptions,
        isFetching: isSubscriptionsFetching,
        isSuccess: isSubscriptionsSuccess,
    } = useGetSubscriptionsQuery(
        { includeDisabled: true },
        { refetchOnMountOrArgChange: true },
    );

    const [createSubscription, createSubscriptionResult] = useCreateSubscriptionMutation();
    const [updateSubscription, updateSubscriptionResult] = useUpdateSubscriptionMutation();
    const [deleteSubscription] = useDeleteSubscriptionMutation();

    useEffect(() => {
        if (isSubscriptionsSuccess) {
            setDataSource(subscriptions.data);
        }
    }, [isSubscriptionsFetching]);

    const showModal = () => {
        setMode(Actions.CREATE);
        setOpen(true);
    };

    const hideModal = () => {
        setOpen(false);
        subscriptionForm.resetFields();
        setMode("");
    };

    const onSave = async () => {
        try {
            await subscriptionForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = subscriptionForm.getFieldsValue();

        if (mode === Actions.CREATE) {
            try {
                await createSubscription(formFields).unwrap();
                setOpen(false);
                subscriptionForm.resetFields();
            } catch (e) { }
        } else {
            try {
                await updateSubscription({ id: editId, body: formFields }).unwrap();
                setOpen(false);
                subscriptionForm.resetFields();
            } catch (e) { }
        }
    };

    const handleEdit = (record: any) => {
        setMode(Actions.EDIT);
        setOpen(true);
        if (record.id) setEditId(record.id);
        subscriptionForm.setFieldsValue(record);
    };

    const handleDelete = async (record: any) => {
        try {
            await deleteSubscription({ id: record.id }).unwrap();
        } catch (e) { }
    };

    return (
        <>
            <div className="flex justify-between items-center gap-3 pb-6">
                <div className="text-headline">{"Subscriptions"}</div>
                <Button type="primary" size="large" className="flex-end" onClick={showModal}>
                    <div className="flex items-center gap-1.5">
                        <Icon icon="add" className="h-4.5 w-4.5" />
                        <div>{"Add Subscription"}</div>
                    </div>
                </Button>
            </div>
            <div className="flex justify-between flex-wrap items-center gap-4 pb-4">
                <div className="flex items-center gap-x-4">
                    <div className="font-medium">
                        {dataSource?.length}{" "}
                        {globalUtils.checkIsSingular(dataSource.length)
                            ? "Subscription"
                            : "Subscriptions"}
                    </div>
                </div>
            </div>
            <div>
                <HrnModal
                    open={open}
                    title={`${mode === Actions.CREATE ? "Add" : "Edit"} Subscription`}
                    width={520}
                    isLoading={
                        (createSubscriptionResult.isLoading ||
                            updateSubscriptionResult.isLoading) as boolean
                    }
                    onCancel={hideModal}
                    onSave={onSave}
                >
                    <div className="mt-6">
                        <Form
                            form={subscriptionForm}
                            layout="vertical"
                            name="subscriptionForm"
                            validateMessages={validateMessages}
                        >
                            <SubscriptionForm mode={mode} subscriptionForm={subscriptionForm} />
                        </Form>
                    </div>
                </HrnModal>
            </div>
            <div>
                <Table
                    rowKey="id"
                    columns={tableUtils.buildColumns(
                        isSubscriptionsSuccess ? dataSource : [],
                        SubscriptionsColumn,
                        tableUtils.editDeleteActionColumns(handleEdit, handleDelete),
                        [],
                        undefined,
                        { serialNo: true }
                    )}
                    dataSource={dataSource}
                    size="middle"
                    className="ant_table"
                    loading={{
                        spinning: isSubscriptionsFetching as boolean,
                        indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                    }}
                    pagination={false}
                />
            </div>
        </>
    );
};

export default Subscription;